/*
  paymentSuccessLock.js  (hosted payment page, src/utils/paymentSuccessLock.js)
  ---------------------------------------------------------------------------
  Terminal "Payment Successful" page for payment-link payments.

  Payment-link tabs are opened by the customer (WhatsApp, email, a merchant's
  site...), so script can never close them. Instead of a popup over the
  checkout, a successful payment-link payment lands on /payment-success and
  stays there. The customer must not be able to go Back into the checkout or
  the payment-link form (and pay again) or Forward anywhere.

  How the lock works:
    1. goToPaymentSuccessPage() stores a flag in sessionStorage (per-tab) and
       does window.location.replace(), so the checkout entry in history is
       overwritten by the success page.
    2. The success page pushes a guard history entry and re-pushes it on every
       popstate, so Back keeps the customer on the page.
    3. Some browsers (Chrome's history-manipulation intervention) skip guard
       entries that were added without a user click. If Back still reaches one
       of our earlier pages, enforcePaymentSuccessLock() (run in index.js
       before React renders) sees a back/forward navigation with the flag set
       and bounces straight back to /payment-success. The pageshow listener
       covers the same case when the browser restores the page from bfcache.
    4. A fresh navigation in the same tab (customer opens a new payment link)
       clears the flag, so a new payment is never blocked.
*/

export const PAYMENT_SUCCESS_PATH = "/payment-success";
const FLAG_KEY = "wq_paymentLinkSuccess";

export const hasPaymentSuccessFlag = () => {
  try {
    return sessionStorage.getItem(FLAG_KEY) === "true";
  } catch (e) {
    return false;
  }
};

const clearPaymentSuccessFlag = () => {
  try {
    sessionStorage.removeItem(FLAG_KEY);
  } catch (e) {
    // storage unavailable: nothing to clear
  }
};

// Call AFTER any sessionStorage.clear() in the success flow, or the flag is lost.
export const goToPaymentSuccessPage = () => {
  try {
    sessionStorage.setItem(FLAG_KEY, "true");
  } catch (e) {
    // storage blocked: the page will still render once, but the back/forward
    // lock can't be enforced across reloads.
  }
  window.location.replace(PAYMENT_SUCCESS_PATH);
};

const isBackForwardNavigation = () => {
  try {
    const [entry] = performance.getEntriesByType("navigation");
    if (entry?.type) return entry.type === "back_forward";
    // Older browsers: PerformanceNavigation.TYPE_BACK_FORWARD === 2
    return performance.navigation?.type === 2;
  } catch (e) {
    return false;
  }
};

const isOnSuccessPage = () => window.location.pathname === PAYMENT_SUCCESS_PATH;

/*
  Run once at startup, before rendering. Returns true when it is redirecting
  to the success page, in which case the app should not render at all (so the
  checkout / payment-link page doesn't fire API calls on its way out).
*/
export const enforcePaymentSuccessLock = () => {
  if (!hasPaymentSuccessFlag() || isOnSuccessPage()) return false;

  if (isBackForwardNavigation()) {
    window.location.replace(PAYMENT_SUCCESS_PATH);
    return true;
  }

  // Fresh visit to another page in this tab (e.g. a new payment link):
  // the previous payment is done, don't hold this tab hostage.
  clearPaymentSuccessFlag();
  return false;
};

// Back/Forward can restore a page from the bfcache without re-running any
// startup code; pageshow (persisted) is the only signal we get then.
export const installPaymentSuccessBfcacheGuard = () => {
  window.addEventListener("pageshow", (event) => {
    if (event.persisted && hasPaymentSuccessFlag() && !isOnSuccessPage()) {
      window.location.replace(PAYMENT_SUCCESS_PATH);
    }
  });
};
